import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { useDisableStudent, useInstitutionStudents } from '../hooks/useInstitution'
import type { InstitutionStudent } from '../types'

function fullName(student: InstitutionStudent): string {
  return [student.first_name, student.last_name].filter(Boolean).join(' ')
}

export function StudentTable() {
  const { t } = useTranslation()
  const { data: students, isLoading, isError } = useInstitutionStudents()
  const disable = useDisableStudent()

  if (isError) {
    return <Alert variant="error">{t('institution.students.load_error')}</Alert>
  }

  if (isLoading) {
    return <div className="h-32 animate-pulse rounded-2xl bg-surface-container" />
  }

  if (!students || students.length === 0) {
    return (
      <div className="rounded-3xl bg-surface-low p-8 text-center">
        <p className="font-medium text-on-surface">{t('institution.students.empty')}</p>
        <p className="mt-1 text-sm text-on-surface-variant">{t('institution.students.empty_hint')}</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-sm">
        <caption className="sr-only">{t('institution.students.title')}</caption>
        <thead>
          <tr className="border-b border-outline-variant text-left text-on-surface-variant">
            <th scope="col" className="py-2 pr-3 font-medium">
              {t('institution.students.name')}
            </th>
            <th scope="col" className="py-2 pr-3 font-medium">
              {t('institution.students.email')}
            </th>
            <th scope="col" className="py-2 pr-3 font-medium">
              {t('institution.students.status')}
            </th>
            <th scope="col" className="py-2 text-right font-medium">
              {t('institution.students.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map((student) => (
            <tr key={student.user_id} className="border-b border-outline-variant/50">
              <td className="py-3 pr-3 font-medium text-on-surface">{fullName(student)}</td>
              <td className="py-3 pr-3 text-on-surface-variant">{student.email}</td>
              <td className="py-3 pr-3">
                <span
                  className={
                    student.status === 'active'
                      ? 'rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-semibold text-secondary'
                      : 'rounded-full bg-surface-container px-2 py-0.5 text-xs font-semibold text-outline'
                  }
                >
                  {t(`institution.students.statuses.${student.status}`)}
                </span>
              </td>
              <td className="py-3 text-right">
                {student.status === 'active' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => disable.mutate(student.user_id)}
                    aria-label={t('institution.students.disable_user', { name: fullName(student) })}
                  >
                    {t('institution.students.disable')}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
